import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Sparkles, Plus, Trash2, Save, X, Loader2 } from 'lucide-react';

const FIELD_TYPES = ['string', 'number', 'date', 'email', 'phone', 'select'];

const emptyField = () => ({
    id: null,
    field_name: '',
    label: '',
    field_type: 'string',
    required: false,
    display_order: 0,
    regex_pattern: '',
    prompt_hint: '',
    examples: '',
    active: true
});

const CatcherFields = () => {
    const { authFetch } = useAuth();
    const [fields, setFields] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const res = await authFetch('/api/catcher/fields');
            if (res.ok) {
                const data = await res.json();
                setFields(data);
            }
        } finally {
            setLoading(false);
        }
    }, [authFetch]);

    useEffect(() => { reload(); }, [reload]);

    const startNew = () => {
        setEditing({ ...emptyField(), display_order: fields.length });
        setError(null);
    };

    const startEdit = async (field) => {
        // Cargar examples actuales
        try {
            const res = await authFetch(`/api/catcher/fields/${field.id}/examples`);
            let examples = '';
            if (res.ok) {
                const data = await res.json();
                examples = (data.examples || []).join('\n');
            }
            setEditing({
                ...field,
                regex_pattern: field.regex_pattern || '',
                prompt_hint: field.prompt_hint || '',
                examples
            });
            setError(null);
        } catch {
            setEditing({ ...field, examples: '' });
        }
    };

    const validate = (f) => {
        if (!f.field_name.trim()) return 'El identificador es obligatorio';
        if (!/^[a-z][a-z0-9_]*$/.test(f.field_name)) return 'field_name debe ser snake_case (a-z, 0-9, _)';
        if (!f.label.trim()) return 'El label es obligatorio';
        const examplesArr = f.examples.split('\n').map(s => s.trim()).filter(Boolean);
        const hasRegex = !!f.regex_pattern.trim();
        const hasHint = !!f.prompt_hint.trim();
        if (!hasRegex && !hasHint && examplesArr.length < 3) {
            return 'Necesitas regex, prompt_hint o al menos 3 ejemplos';
        }
        return null;
    };

    const save = async () => {
        const err = validate(editing);
        if (err) { setError(err); return; }
        setSaving(true);
        setError(null);
        try {
            const examplesArr = editing.examples.split('\n').map(s => s.trim()).filter(Boolean);
            const payload = {
                field_name: editing.field_name.trim(),
                label: editing.label.trim(),
                field_type: editing.field_type,
                required: !!editing.required,
                display_order: parseInt(editing.display_order, 10) || 0,
                regex_pattern: editing.regex_pattern.trim() || null,
                prompt_hint: editing.prompt_hint.trim() || null,
                active: !!editing.active,
                examples: examplesArr
            };
            const url = editing.id ? `/api/catcher/fields/${editing.id}` : '/api/catcher/fields';
            const method = editing.id ? 'PUT' : 'POST';
            const res = await authFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                setError(data.error || `Error ${res.status}`);
                return;
            }
            setEditing(null);
            await reload();
        } finally {
            setSaving(false);
        }
    };

    const remove = async (field) => {
        if (!window.confirm(`¿Eliminar el campo "${field.label}"? Esta acción no es reversible.`)) return;
        const res = await authFetch(`/api/catcher/fields/${field.id}`, { method: 'DELETE' });
        if (res.ok) await reload();
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Sparkles className="text-fuchsia-400" size={22} />
                        Campos del Catcher
                    </h1>
                    <p className="text-sm text-slate-400 mt-1">
                        Configura qué información debe extraer el Catcher de las conversaciones de WhatsApp.
                    </p>
                </div>
                <button
                    onClick={startNew}
                    className="flex items-center gap-2 px-4 py-2 bg-primary/20 hover:bg-primary/30 border border-primary/40 text-primary rounded-xl text-sm font-bold uppercase tracking-wider"
                >
                    <Plus size={16} /> Nuevo campo
                </button>
            </div>

            {loading && <div className="text-slate-500 text-sm">Cargando…</div>}

            {!loading && fields.length === 0 && (
                <div className="p-6 border border-slate-800 rounded-xl text-center text-slate-500 text-sm">
                    Aún no hay campos. Crea el primero para empezar.
                </div>
            )}

            <div className="grid gap-3">
                {fields.map(f => (
                    <div key={f.id} className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 flex items-center justify-between">
                        <div>
                            <div className="font-semibold text-sm">
                                {f.label}
                                <span className="text-xs text-slate-500 font-mono ml-2">{f.field_name}</span>
                                {!f.active && <span className="ml-2 text-[10px] uppercase text-amber-400">inactivo</span>}
                                {f.required && <span className="ml-2 text-[10px] uppercase text-rose-400">requerido</span>}
                            </div>
                            <div className="text-xs text-slate-500 mt-1">
                                tipo: {f.field_type} · ejemplos: {f.example_count || 0}
                                {f.regex_pattern && <span> · regex</span>}
                                {f.prompt_hint && <span> · hint</span>}
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => startEdit(f)}
                                className="px-3 py-1.5 text-xs bg-slate-800 hover:bg-slate-700 rounded-lg"
                            >
                                Editar
                            </button>
                            <button
                                onClick={() => remove(f)}
                                className="p-1.5 text-rose-400 hover:bg-rose-500/10 rounded-lg"
                                title="Eliminar"
                            >
                                <Trash2 size={14} />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Modal de edición */}
            {editing && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-slate-950 border border-slate-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="p-5 border-b border-slate-800 flex items-center justify-between">
                            <h2 className="font-bold">{editing.id ? 'Editar campo' : 'Nuevo campo'}</h2>
                            <button onClick={() => setEditing(null)} className="p-1 hover:bg-slate-800 rounded">
                                <X size={16} />
                            </button>
                        </div>
                        <div className="p-5 space-y-4">
                            {error && (
                                <div className="p-3 bg-rose-500/10 border border-rose-500/30 rounded-lg text-rose-300 text-xs">
                                    {error}
                                </div>
                            )}
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Identificador (snake_case)">
                                    <input
                                        type="text"
                                        value={editing.field_name}
                                        disabled={!!editing.id}
                                        onChange={(e) => setEditing({ ...editing, field_name: e.target.value })}
                                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm font-mono disabled:opacity-50"
                                        placeholder="full_name"
                                    />
                                </Field>
                                <Field label="Label visible">
                                    <input
                                        type="text"
                                        value={editing.label}
                                        onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm"
                                        placeholder="Nombre completo"
                                    />
                                </Field>
                                <Field label="Tipo">
                                    <select
                                        value={editing.field_type}
                                        onChange={(e) => setEditing({ ...editing, field_type: e.target.value })}
                                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm"
                                    >
                                        {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </Field>
                                <Field label="Orden">
                                    <input
                                        type="number"
                                        value={editing.display_order}
                                        onChange={(e) => setEditing({ ...editing, display_order: e.target.value })}
                                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm"
                                    />
                                </Field>
                            </div>

                            <div className="flex items-center gap-4">
                                <label className="flex items-center gap-2 text-xs">
                                    <input
                                        type="checkbox"
                                        checked={editing.required}
                                        onChange={(e) => setEditing({ ...editing, required: e.target.checked })}
                                    />
                                    Requerido
                                </label>
                                <label className="flex items-center gap-2 text-xs">
                                    <input
                                        type="checkbox"
                                        checked={editing.active}
                                        onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                                    />
                                    Activo
                                </label>
                            </div>

                            <Field label="Regex (opcional, capa 1 — más rápido y seguro)">
                                <input
                                    type="text"
                                    value={editing.regex_pattern}
                                    onChange={(e) => setEditing({ ...editing, regex_pattern: e.target.value })}
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm font-mono"
                                    placeholder="\\b\\d{8}[A-Z]\\b"
                                />
                            </Field>

                            <Field label="Prompt hint (opcional, ayuda al LLM en capa 3)">
                                <textarea
                                    value={editing.prompt_hint}
                                    onChange={(e) => setEditing({ ...editing, prompt_hint: e.target.value })}
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm h-20"
                                    placeholder="El nombre completo de la persona, sin tratamientos."
                                />
                            </Field>

                            <Field label="Ejemplos (uno por línea, mínimo 3 si no hay regex/hint)">
                                <textarea
                                    value={editing.examples}
                                    onChange={(e) => setEditing({ ...editing, examples: e.target.value })}
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm h-32 font-mono"
                                    placeholder={'me llamo Juan\nsoy María García\nmi nombre es Pedro'}
                                />
                                <p className="text-[10px] text-slate-500 mt-1">
                                    Se vectorizan al guardar (puede tardar unos segundos).
                                </p>
                            </Field>
                        </div>

                        <div className="p-5 border-t border-slate-800 flex justify-end gap-2">
                            <button
                                onClick={() => setEditing(null)}
                                className="px-4 py-2 text-sm bg-slate-800 hover:bg-slate-700 rounded-lg"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={save}
                                disabled={saving}
                                className="flex items-center gap-2 px-4 py-2 text-sm bg-primary/20 hover:bg-primary/30 border border-primary/40 text-primary rounded-lg font-bold uppercase tracking-wider disabled:opacity-50"
                            >
                                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                                Guardar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const Field = ({ label, children }) => (
    <div>
        <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">
            {label}
        </label>
        {children}
    </div>
);

export default CatcherFields;
